<template>
    <modal
        ref="modal"
        :title="modalTitle"
        class-name="customer-product-modal"
        @close="handleClose"
    >
        <form
            ref="form"
            @submit.prevent="saveProduct"
        >
            <div class="form-group">
                <label for="product_id">Filter products</label>
                <input
                    v-if="!form.editingId"
                    v-model="productSearch"
                    type="text"
                    class="form-control mb-1"
                    placeholder="Filter products..."
                >
                <label for="product_id">Select Product</label>
                <select
                    v-if="!form.editingId"
                    id="product_id"
                    v-model="form.product_id"
                    class="form-control"
                    :class="{ 'is-invalid': errors.product_id }"
                    @change="onProductChange"
                >
                    <option
                        :value="null"
                        disabled
                    >
                        Select product
                    </option>
                    <optgroup
                        v-for="group in filteredProductsByCategory"
                        :key="group.categoryName"
                        :label="group.categoryName"
                    >
                        <option
                            v-for="product in group.products"
                            :key="product.id"
                            :value="product.id"
                        >
                            {{ product.name }}{{ product.code ? ' - ' + product.code : '' }}
                        </option>
                    </optgroup>
                </select>
                <input
                    v-else
                    type="text"
                    class="form-control"
                    :value="form.productDisplayName"
                    readonly
                    disabled
                >
                <error
                    :errors="errors"
                    name="product_id"
                />
            </div>
            <div class="form-group">
                <label for="price">Price</label>
                <div class="input-group">
                    <div class="input-group-prepend">
                        <span class="input-group-text">£</span>
                    </div>
                    <input
                        id="price"
                        v-model="form.price"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="10.00"
                        class="form-control"
                        style="max-width: 10em"
                        :class="{ 'is-invalid': errors.price }"
                    >
                </div>
                <small
                    v-if="originalPriceHint"
                    class="form-text text-muted"
                >
                    {{ originalPriceHint }}
                </small>
                <error
                    :errors="errors"
                    name="price"
                />
            </div>
        </form>
        <template slot="footer">
            <div class="d-flex w-100 justify-content-between">
                <div>
                    <button
                        v-if="form.editingId"
                        type="button"
                        class="btn btn-outline-danger"
                        :disabled="saving"
                        @click="confirmDelete"
                    >
                        <i class="fa fa-trash" /> Delete
                    </button>
                </div>
                <div>
                    <button
                        type="button"
                        class="btn btn-secondary"
                        @click="$refs.modal.close()"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="saving"
                        @click="saveProduct"
                    >
                        {{ form.editingId ? 'Save' : 'Add' }}
                    </button>
                </div>
            </div>
        </template>
    </modal>
</template>
<script>
import CustomerProductService from '../../../services/CustomerProductService';
import UsesErrors from '../../../mixins/UsesErrors';
import UsesInfoMessage from '../../../mixins/UsesInfoMessage';
import Modal from '../../modals/ModalComponent.vue';

export default {
    components: {
        Modal,
    },
    mixins: [UsesErrors, UsesInfoMessage],
    props: {
        customerId: {
            type: [String, Number],
            required: true,
        },
        products: {
            type: Array,
            default: () => [],
        },
        customerProducts: {
            type: Array,
            default: () => [],
        },
        editingProduct: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            productSearch: '',
            form: {
                editingId: null,
                product_id: null,
                productDisplayName: '',
                price: '',
            },
            saving: false,
        };
    },
    computed: {
        modalTitle() {
            return this.form.editingId ? 'Edit Product' : 'Add Product';
        },
        selectedProduct() {
            if (!this.form.product_id) return null;
            return this.products.find(p => p.id == this.form.product_id);
        },
        originalPriceHint() {
            if (!this.selectedProduct) return null;
            const original = parseFloat(this.selectedProduct.unit_price);
            const current = parseFloat(this.form.price);
            if (isNaN(current) || original === current) return null;
            return `(Original price £${original.toFixed(2)})`;
        },
        productsByCategory() {
            const usedProductIds = this.customerProducts
                .filter(cp => !this.form.editingId || cp.id !== this.form.editingId)
                .map(cp => cp.product_id);
            const available = this.products.filter(p => !usedProductIds.includes(p.id));

            const grouped = window._.groupBy(available, p => {
                return (p.category && p.category.name) || 'Uncategorized';
            });

            return Object.keys(grouped)
                .sort((a, b) => {
                    if (a === 'Uncategorized') return 1;
                    if (b === 'Uncategorized') return -1;
                    return a.localeCompare(b);
                })
                .map(categoryName => ({
                    categoryName,
                    products: window._.sortBy(grouped[categoryName], 'name'),
                }));
        },
        filteredProductsByCategory() {
            if (!this.productSearch) {
                return this.productsByCategory;
            }
            var search = this.productSearch.toLowerCase();
            return this.productsByCategory
                .map(function (group) {
                    return {
                        categoryName: group.categoryName,
                        products: group.products.filter(function (p) {
                            var nameMatch = p.name && p.name.toLowerCase().indexOf(search) !== -1;
                            var codeMatch = p.code && p.code.toLowerCase().indexOf(search) !== -1;
                            return nameMatch || codeMatch;
                        }),
                    };
                })
                .filter(function (group) {
                    return group.products.length > 0;
                });
        },
    },
    mounted() {
        if (this.editingProduct) {
            this.form = {
                editingId: this.editingProduct.id,
                product_id: this.editingProduct.product_id,
                productDisplayName: (this.editingProduct.product ? this.editingProduct.product.name : '') +
                    (this.editingProduct.product && this.editingProduct.product.code ? ' - ' + this.editingProduct.product.code : ''),
                price: this.editingProduct.price,
            };
        }
        this.$refs.modal.open();
    },
    methods: {
        resetForm() {
            this.form = {
                editingId: null,
                product_id: null,
                productDisplayName: '',
                price: '',
            };
        },
        onProductChange() {
            if (this.selectedProduct && this.selectedProduct.unit_price != null) {
                this.form.price = this.selectedProduct.unit_price;
            }
        },
        handleClose() {
            this.$emit('close');
        },
        confirmDelete() {
            if (!window.confirm('Are you sure you want to remove this product from the customer?')) {
                return;
            }
            this.saving = true;
            CustomerProductService.destroy(this.customerId, this.form.editingId).then(() => {
                this.$refs.modal && this.$refs.modal.close();
                this.resetForm();
                this.$emit('deleted');
                this.setInfoMessage('Product removed successfully');
            }).catch(err => {
                if (err.response && err.response.data && err.response.data.errors) {
                    this.captureRequestErrors(err);
                } else {
                    this.showErrorMessage('Failed to remove product');
                }
            }).then(() => {
                this.saving = false;
            });
        },
        saveProduct() {
            const validationErrors = {};
            if (!this.form.editingId && !this.form.product_id) {
                validationErrors.product_id = ['Please select a product'];
            }
            if (this.form.price === '' || this.form.price === null) {
                validationErrors.price = ['Please enter a price'];
            }
            if (Object.keys(validationErrors).length) {
                this.setErrors(validationErrors);
                return;
            }
            this.clearErrors();
            this.saving = true;

            const promise = this.form.editingId
                ? CustomerProductService.update(this.customerId, this.form.editingId, { price: this.form.price })
                : CustomerProductService.create(this.customerId, {
                    product_id: this.form.product_id,
                    price: this.form.price,
                });

            const wasEditing = !!this.form.editingId;
            promise.then(() => {
                this.$refs.modal && this.$refs.modal.close();
                this.resetForm();
                this.$emit('saved', { wasEditing });
                this.setInfoMessage(wasEditing ? 'Product updated successfully' : 'Product added successfully');
            }).catch(err => {
                this.captureRequestErrors(err);
            }).then(() => {
                this.saving = false;
            });
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/office/customers/CustomerProductModal.vue